import { supabase } from '@/integrations/supabase/client';
import { devLog } from '@/utils/logger';
import type { 
  CompensationValidationResult, 
  ValidationWarning, 
  ValidationError, 
  PayrollImpactEstimate 
} from './types';
import type { CompensationRule } from '@/services/compensation/types';

export const validationService = {
  /**
   * Validate compensation rule changes before applying them to payroll
   */
  validateCompensationChanges: async (
    companyId: string,
    changes: CompensationRule[]
  ): Promise<CompensationValidationResult> => {
    devLog('[validationService] Validating compensation changes for company:', companyId);
    
    const warnings: ValidationWarning[] = [];
    const errors: ValidationError[] = [];
    let isValid = true;

    try {
      // Check for overlapping rules
      const overlapValidation = await validationService.validateRuleOverlaps(changes);
      if (overlapValidation?.errors && overlapValidation.errors.length > 0) {
        errors.push(...overlapValidation.errors);
        isValid = false;
      }
      if (overlapValidation?.warnings) {
        warnings.push(...overlapValidation.warnings);
      }

      // Validate rate changes
      const rateValidation = await validationService.validateRateChanges(changes);
      if (rateValidation?.errors && rateValidation.errors.length > 0) {
        errors.push(...rateValidation.errors);
        isValid = false;
      }
      if (rateValidation?.warnings) {
        warnings.push(...rateValidation.warnings);
      }

      // Calculate payroll impact
      const impactEstimate = await validationService.estimatePayrollImpact(companyId, changes);

      return {
        isValid,
        warnings,
        errors,
        estimated_impact: impactEstimate
      };
    } catch (error) {
      console.error('[validationService] Error during validation:', error);
      return {
        isValid: false,
        warnings: [],
        errors: [{
          id: 'validation_error',
          type: 'invalid_rate',
          message: 'Failed to validate compensation changes. Please try again.',
        }]
      };
    }
  },

  /**
   * Validate for overlapping compensation rules
   */
  validateRuleOverlaps: async (rules: CompensationRule[]) => {
    const warnings: ValidationWarning[] = [];
    const errors: ValidationError[] = [];

    // Validation is now handled at assignment level since rules are templates
    // Rule overlaps are checked when assigning rules to employees
    return { warnings, errors };
  },

  /**
   * Validate rate changes for reasonableness
   */
  validateRateChanges: async (rules: CompensationRule[]) => {
    const warnings: ValidationWarning[] = [];
    const errors: ValidationError[] = [];

    for (const rule of rules) {
      // Check for negative rates
      if (rule.base_rate && rule.base_rate < 0) {
        errors.push({
          id: `negative_rate_${rule.id}`,
          type: 'invalid_rate',
          message: 'Base rate cannot be negative',
          field: 'base_rate'
        });
      }

      // Check for unrealistic multipliers
      if (rule.multiplier_rate && (rule.multiplier_rate < 0 || rule.multiplier_rate > 10)) {
        warnings.push({
          id: `unusual_multiplier_${rule.id}`,
          type: 'rate_change',
          message: `Unusual multiplier rate: ${rule.multiplier_rate}x. Please verify this is correct.`,
          severity: 'medium'
        });
      }

      // Check for high percentage rates
      if (rule.percentage_rate && rule.percentage_rate > 100) {
        warnings.push({
          id: `high_percentage_${rule.id}`,
          type: 'rate_change',
          message: `High percentage rate: ${rule.percentage_rate}%. Please verify this is correct.`,
          severity: 'high'
        });
      }

      // Validate threshold hours
      if (rule.threshold_hours && rule.threshold_hours < 0) {
        errors.push({
          id: `negative_threshold_${rule.id}`,
          type: 'invalid_rate',
          message: 'Threshold hours cannot be negative',
          field: 'threshold_hours'
        });
      }
    }

    return { warnings, errors };
  },

  /**
   * Estimate payroll impact of compensation changes
   */
  estimatePayrollImpact: async (
    companyId: string, 
    changes: CompensationRule[]
  ): Promise<PayrollImpactEstimate> => {
    // Since rules no longer have profile_id, we can't get affected employees directly
    // This would need to be updated to use the assignment table
    const affectedEmployees: string[] = []; // Placeholder - would query assignments
    
    // Get recent payroll data for comparison
    const threeMonthsAgo = new Date();
    threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
    
    const { data: recentPayslips } = await supabase
      .from('payslips')
      .select('profile_id, gross_pay, net_pay')
      .eq('company_id', companyId)
      .gte('start_date', threeMonthsAgo.toISOString())
      .in('profile_id', affectedEmployees);

    const recentAverage = recentPayslips?.reduce((sum, payslip) => 
      sum + Number(payslip.gross_pay || 0), 0) || 0;

    // Rough estimate based on rule changes
    // This is a simplified calculation - in practice, you'd want more sophisticated modeling
    const estimatedIncrease = changes.reduce((total, rule) => {
      if (rule.rule_type === 'hourly_rate' && rule.base_rate) {
        // Assume 40 hours/week * 4.33 weeks/month
        return total + (rule.base_rate * 40 * 4.33);
      }
      if (rule.rule_type === 'overtime' && rule.multiplier_rate) {
        // Estimate 5 hours overtime per week
        return total + (rule.base_rate || 20) * (rule.multiplier_rate - 1) * 5 * 4.33;
      }
      return total;
    }, 0);

    return {
      total_cost_change: estimatedIncrease,
      affected_employee_count: affectedEmployees.length,
      estimated_gross_change: estimatedIncrease,
      estimated_net_change: estimatedIncrease * 0.75, // Rough net after taxes
      comparison_period: 'monthly'
    };
  },

  /**
   * Check if two date ranges overlap
   */
  checkDateOverlap: (
    start1: string, end1: string | null,
    start2: string, end2: string | null
  ): boolean => {
    const s1 = new Date(start1);
    const e1 = end1 ? new Date(end1) : new Date('2099-12-31');
    const s2 = new Date(start2);
    const e2 = end2 ? new Date(end2) : new Date('2099-12-31');

    return s1 <= e2 && s2 <= e1;
  },

  /**
   * Validate payslip before generation
   */
  validatePayslipGeneration: async (
    profileId: string,
    startDate: string,
    endDate: string
  ): Promise<CompensationValidationResult> => {
    devLog('[validationService] Validating payslip generation for:', profileId);
    
    const warnings: ValidationWarning[] = [];
    const errors: ValidationError[] = [];

    try {
      // Check for assigned rules using the assignment table instead of profile_id
      const { data: assignments } = await supabase
        .from('compensation_rule_assignments')
        .select(`
          rule_id,
          compensation_rules (*)
        `)
        .eq('profile_id', profileId)
        .eq('is_active', true);

      if (!assignments || assignments.length === 0) {
        warnings.push({
          id: 'no_compensation_rules',
          type: 'missing_data',
          message: 'No active compensation rules found. Payslip will use legacy pay rate.',
          severity: 'medium'
        });
      }

      // Check for time entries in the period
      const { data: timeEntries } = await supabase
        .from('time_entries')
        .select('id')
        .eq('profile_id', profileId)
        .gte('date', startDate)
        .lte('date', endDate)
        .limit(1);

      if (!timeEntries || timeEntries.length === 0) {
        warnings.push({
          id: 'no_time_entries',
          type: 'missing_data',
          message: 'No time entries found for the payroll period.',
          severity: 'high'
        });
      }

      return {
        isValid: errors.length === 0,
        warnings,
        errors
      };
    } catch (error) {
      console.error('[validationService] Error validating payslip generation:', error);
      return {
        isValid: false,
        warnings: [],
        errors: [{
          id: 'validation_error',
          type: 'invalid_rate',
          message: 'Failed to validate payslip generation. Please try again.',
        }]
      };
    }
  }
};